import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, FileDown, Coins } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/api";
import { TREASURY_CATEGORIES, formatDate } from "@/constants";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n ?? 0);
const EMPTY_FORM = { type: "entree", amount: "", category: "Cotisations", label: "", date: new Date().toISOString().slice(0, 10) };

export default function AdminTreasury() {
  const [data, setData] = useState({ transactions: [], entrees: 0, sorties: 0, balance: 0, cash_float: 0, balance_with_cash: 0 });
  const [cashInput, setCashInput] = useState("0");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/treasury").then((r) => { setData(r.data); setCashInput(String(r.data.cash_float ?? 0)); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/treasury", { ...form, amount: parseFloat(String(form.amount).replace(",", ".")) });
      toast.success("Opération enregistrée");
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer l'opération « ${t.label} » ?`)) return;
    try {
      await api.delete(`/admin/treasury/${t.id}`);
      toast.success("Opération supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCash = async () => {
    try {
      await api.post("/admin/treasury/cash-float", { amount: parseFloat(String(cashInput).replace(",", ".")) || 0 });
      toast.success("Fonds de caisse enregistré");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const stats = [
    { label: "Solde actuel (banque)", value: data.balance, icon: Wallet, color: data.balance >= 0 ? "text-emerald-700 bg-emerald-100" : "text-red-700 bg-red-100", id: "balance" },
    { label: "Total entrées", value: data.entrees, icon: TrendingUp, color: "text-emerald-700 bg-emerald-100", id: "entrees" },
    { label: "Total sorties", value: data.sorties, icon: TrendingDown, color: "text-red-700 bg-red-100", id: "sorties" },
    { label: "Solde avec fonds de caisse", value: data.balance_with_cash, icon: Coins, color: "text-amber-700 bg-amber-100", id: "balance-cash" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-treasury-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-treasury-title">Trésorerie</h1>
          <p className="text-sm text-slate-500 mt-1">Suivi simple des entrées et sorties (pas de comptabilité officielle).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="treasury-export-btn">
            <a href={`${API_BASE}/admin/treasury/export`} download>
              <FileDown className="h-4 w-4 mr-2" />Exporter CSV
            </a>
          </Button>
          <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => { setForm(EMPTY_FORM); setDialogOpen(true); }} data-testid="transaction-create-btn">
            <Plus className="h-4 w-4 mr-2" />Nouvelle opération
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color, id }) => (
          <Card key={id} className="border-slate-200" data-testid={`treasury-stat-${id}`}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">{label}</p>
                <p className="font-heading text-xl font-bold text-slate-900">{fmt(value)}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-amber-200 bg-amber-50/60" data-testid="cash-float-card">
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Coins className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold text-amber-900">Fonds de caisse espèces (hors compte bancaire)</p>
            <p className="text-xs text-amber-700">Liquide conservé physiquement — compté à part du solde bancaire.</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.01" min="0" className="w-32 bg-white" value={cashInput}
              onChange={(e) => setCashInput(e.target.value)} data-testid="cash-float-input" />
            <Button variant="outline" onClick={saveCash} data-testid="cash-float-save-btn">Enregistrer</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="transactions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.transactions.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8" data-testid="transactions-empty">
                  Aucune opération enregistrée.
                </TableCell></TableRow>
              )}
              {data.transactions.map((t) => (
                <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(t.date)}</TableCell>
                  <TableCell>
                    <Badge className={t.type === "entree"
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-red-100 text-red-800 border border-red-200 hover:bg-red-100"}>
                      {t.type === "entree" ? "Entrée" : "Sortie"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{t.category}</TableCell>
                  <TableCell className="text-sm font-medium">{t.label}</TableCell>
                  <TableCell className={`text-right font-semibold ${t.type === "entree" ? "text-emerald-700" : "text-red-700"}`}>
                    {t.type === "entree" ? "+" : "−"}{fmt(t.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => remove(t)} data-testid={`transaction-delete-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle opération</DialogTitle>
            <DialogDescription>Saisie d'une entrée ou sortie d'argent pour le suivi de trésorerie.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="transaction-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entree">Entrée</SelectItem>
                    <SelectItem value="sortie">Sortie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Montant (€)</Label>
                <Input type="number" step="0.01" min="0.01" required value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="transaction-form-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="transaction-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TREASURY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="transaction-form-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Libellé</Label>
              <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex : Cotisations septembre" data-testid="transaction-form-label" />
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="transaction-form-submit">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
